import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const location = useLocation()

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
    setActiveDropdown(null)
  }, [location])

  const menuItems = [
    { label: 'Forside', href: '/' },
    {
      label: 'Landbrugsmaskinstation',
      href: '/landbrugsmaskinstation',
      children: [
        { label: 'Jordbehandling', href: '/landbrugsmaskinstation/jordbehandling' },
        { label: 'Såning', href: '/landbrugsmaskinstation/saaning' },
        { label: 'Høst & Presning', href: '/landbrugsmaskinstation/hoest-og-presning' },
        { label: 'Gylle & Gødskning', href: '/landbrugsmaskinstation/gylle-og-goedskning' }
      ]
    },
    {
      label: 'Entreprenør & Anlæg',
      href: '/entreprenoer-og-anlaeg',
      children: [
        { label: 'Jord- & gravearbejde', href: '/entreprenoer-og-anlaeg/jord-og-gravearbejde' },
        { label: 'Belægning', href: '/entreprenoer-og-anlaeg/belaegning' },
        { label: 'Nedbrydning', href: '/entreprenoer-og-anlaeg/nedbrydning' },
        { label: 'Snerydning', href: '/entreprenoer-og-anlaeg/snerydning' }
      ]
    },
    {
      label: 'Maskinudlejning / Salg',
      href: '/maskinudlejning-salg',
      children: [
        { label: 'Maskinpark', href: '/maskinudlejning-salg/maskinpark' }
      ]
    },
    {
      label: 'Om os & Galleri',
      href: '/om-os-galleri',
      children: [
        { label: 'Maskinpark', href: '/maskinudlejning-salg/maskinpark' },
        { label: 'Historie', href: '/om-os-galleri/historie' },
        { label: 'Etableringsstøtte', href: '/om-os-galleri/etableringsstoette' }
      ]
    },
    {
      label: 'Salg af sand/sten og grus',
      href: '/sand-sten-grus',
      children: [
        { label: 'Sand/Flis', href: '/sand-sten-grus/sand-flis' },
        { label: 'Grus', href: '/sand-sten-grus/grus' },
        { label: 'Stenmel', href: '/sand-sten-grus/stenmel' },
        { label: 'Støbemix', href: '/sand-sten-grus/stoebemix' },
        { label: 'Stabilgrus', href: '/sand-sten-grus/stabilgrus' },
        { label: 'Hent selv', href: '/sand-sten-grus/hent-selv' },
        { label: 'Robinie stolper', href: '/sand-sten-grus/robinie-stolper' }
      ]
    },
    { label: 'Kontakt', href: '/kontakt' }
  ]

  const isLinkActive = (href, children) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    if (location.pathname === href) return true
    if (children) {
      return children.some((child) => location.pathname === child.href)
    }
    return location.pathname.startsWith(href)
  }

  const toggleDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index)
  }

  return (
    <>
      {/* Desktop Top Bar: Centered Brand Logo (scrolls away naturally as you scroll down) */}
      <div className="hidden min-[1200px]:block bg-white py-6 border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-center">
          <Link to="/" className="flex items-center justify-center">
            <img
              src="https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/logo-1-7541a179.png"
              alt="Sloth’s Maskinstation"
              className="h-28 w-auto object-contain"
            />
          </Link>
        </div>
      </div>

      {/* Sticky Header Bar: Mobile shows Logo+Hamburger; Desktop shows centered navigation */}
      <header className="sticky top-0 z-50 w-full bg-white shadow-md border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Mobile Bar (Visible below 1200px) */}
          <div className="flex min-[1200px]:hidden items-center justify-between py-3">
            <Link to="/" className="flex items-center">
              <img
                src="https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/logo-1-7541a179.png"
                alt="Sloth’s Maskinstation"
                className="h-12 w-auto object-contain"
              />
            </Link>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-brand-red transition-colors cursor-pointer"
              aria-label="Menu"
            >
              <i className={`fa-regular ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-lg`} />
            </button>
          </div>

          {/* Desktop Navigation (Visible at 1200px and above) */}
          <nav className="hidden min-[1200px]:flex items-center justify-center py-2.5 gap-1.5 flex-wrap">
            {menuItems.map((item, index) => {
              const active = isLinkActive(item.href, item.children)
              return (
                <div key={index} className="relative group">
                  {item.children ? (
                    <>
                      <button
                        className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-bold transition-colors cursor-pointer ${
                          active
                            ? 'text-brand-red bg-red-50/50'
                            : 'text-gray-700 hover:text-brand-red hover:bg-gray-50'
                        }`}
                      >
                        {item.label}
                        <i className="fa-regular fa-chevron-down text-[10px] transition-transform group-hover:rotate-180" />
                      </button>

                      {/* Dropdown Menu */}
                      <div className="absolute left-1/2 -translate-x-1/2 top-full hidden w-64 rounded-xl border border-gray-100 bg-white p-2 shadow-xl group-hover:block animate-fade-in">
                        {item.children.map((child, childIndex) => (
                          <Link
                            key={childIndex}
                            to={child.href}
                            className={`block rounded-lg px-4 py-2.5 text-xs font-semibold transition-colors ${
                              location.pathname === child.href
                                ? 'bg-brand-red text-white'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-brand-red'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={`block rounded-lg px-3 py-2 text-sm font-bold transition-colors ${
                        active
                          ? 'text-brand-red bg-red-50/50'
                          : 'text-gray-700 hover:text-brand-red hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              )
            })}
          </nav>
        </div>
      </header>

      {/* Mobile Navigation Drawer (Visible below 1200px) */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-x-0 bottom-0 top-[65px] z-50 bg-black/50 backdrop-blur-sm min-[1200px]:hidden animate-fade-in"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className="absolute right-0 top-0 w-full max-w-sm h-full overflow-y-auto bg-white p-6 shadow-2xl border-l border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-3">
              {menuItems.map((item, index) => {
                const active = isLinkActive(item.href, item.children)
                return (
                  <div key={index} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    {item.children ? (
                      <>
                        <button
                          onClick={() => toggleDropdown(index)}
                          className={`flex w-full items-center justify-between py-2 text-left text-base font-bold transition-colors cursor-pointer ${
                            active ? 'text-brand-red' : 'text-gray-800'
                          }`}
                        >
                          {item.label}
                          <i
                            className={`fa-regular fa-chevron-down text-xs transition-transform ${
                              activeDropdown === index ? 'rotate-180' : ''
                            }`}
                          />
                        </button>

                        {/* Mobile Dropdown */}
                        {activeDropdown === index && (
                          <div className="mt-2 ml-4 space-y-1 border-l-2 border-brand-red/20 pl-4">
                            {item.children.map((child, childIndex) => (
                              <Link
                                key={childIndex}
                                to={child.href}
                                className={`block py-2 text-sm font-semibold transition-colors ${
                                  location.pathname === child.href
                                    ? 'text-brand-red'
                                    : 'text-gray-600 hover:text-brand-red'
                                }`}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className={`block py-2 text-base font-bold transition-colors ${
                          active ? 'text-brand-red' : 'text-gray-800 hover:text-brand-red'
                        }`}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Mobile Contact Info */}
            <div className="mt-8 rounded-xl bg-gray-50 p-4 space-y-3 text-xs text-gray-600">
              <div className="flex items-center gap-2">
                <i className="fa-regular fa-phone text-brand-red" />
                <a href="tel:004540420286" className="font-bold text-gray-800">
                  +45 4042 0286
                </a>
              </div>
              <div className="flex items-center gap-2">
                <i className="fa-regular fa-envelope text-brand-red" />
                <a href="mailto:slothsmaskinstation@gmail.com" className="font-bold text-gray-800">
                  slothsmaskinstation@gmail.com
                </a>
              </div>
              <div className="flex items-start gap-2">
                <i className="fa-regular fa-location-dot text-brand-red mt-0.5" />
                <span>Gullev Byvej 24, Gullev, 8850 Bjerringbro</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
